import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DesignFolder } from "../../core/models/entities/DesignFolder";
import { Role } from "../../core/models/entities/Role";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { mapToDesignFolder } from "../models/mappers/mapDesignFolder";

export interface DesignFolderRepository {
  // orgId undefined (profile not loaded yet / non-folder user) keeps the
  // query disabled - no request is sent.
  useFolders(orgId: number | undefined): Resource<DesignFolder[]>;
  // Fail-silent cache warm-up sharing useFolders' query definition, so a
  // later useFolders mount is served from cache. Owns its own guard: no-ops
  // without an orgId or for roles that never see the folder bar - callers
  // just say when, not who.
  prefetchFolders(
    orgId: number | undefined,
    role: Role | undefined,
  ): Promise<void>;
  createFolder(
    orgId: number,
    name: string,
    parentId?: number | null,
  ): Promise<DesignFolder>;
  renameFolder(orgId: number, folderId: number, name: string): Promise<void>;
  deleteFolder(orgId: number, folderId: number): Promise<void>;
  // Re-parent a folder (parentId null = top level). The FolderBar guards
  // against self/descendant targets before calling; the backend validates too.
  moveFolder(
    orgId: number,
    folderId: number,
    parentId: number | null,
  ): Promise<void>;
  // Full new sibling order for ONE level (parentId null = top level):
  // orderedIds must contain every folder id of that level.
  reorderFolders(
    orgId: number,
    parentId: number | null,
    orderedIds: number[],
  ): Promise<void>;
  // folderId null = unfile. Also refetches the design list, since a move
  // changes which designs a folder-filtered page contains.
  moveDesignsToFolder(
    orgId: number,
    designIds: number[],
    folderId: number | null,
  ): Promise<void>;
  // Same, for the logo list (the folder tree is shared between the two).
  moveLogosToFolder(
    orgId: number,
    logoIds: number[],
    folderId: number | null,
  ): Promise<void>;
}

export function useDesignFolderRepository(): DesignFolderRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  async function invalidateFolders() {
    await queryClient.invalidateQueries({ queryKey: ["folders"], type: "all" });
  }

  async function invalidateDesigns() {
    await queryClient.invalidateQueries({ queryKey: ["designs"], type: "all" });
  }

  // Single source of truth for the folders query: useFolders and
  // prefetchFolders read the SAME definition, so key/fn/staleTime cannot
  // drift apart (a drifted prefetch would warm an entry the hook never
  // reads - fail-silent, so nothing would ever surface it).
  function foldersQuery(orgId: number) {
    return {
      queryKey: ["folders", orgId],
      queryFn: () =>
        apiClient
          .getDesignFolders(orgId)
          .then((dtos) => dtos.map(mapToDesignFolder)),
      staleTime: 5 * 60 * 1000,
    };
  }

  function useFolders(orgId: number | undefined): Resource<DesignFolder[]> {
    const { queryKey, queryFn, staleTime } = foldersQuery(orgId!);
    return useQuery(queryKey, queryFn, {
      enabled: !!orgId,
      staleTime,
    });
  }

  // prefetchQuery never rejects (React Query swallows prefetch errors), so a
  // failed warm-up costs nothing: useFolders simply fetches as before. The
  // role/orgId guard lives HERE - the folder bar renders for Admin/Vendor
  // only, and the two view models gate on the same rule - so callers cannot
  // drift from the folder bar's actual audience.
  function prefetchFolders(
    orgId: number | undefined,
    role: Role | undefined,
  ): Promise<void> {
    if (!orgId || (role !== Role.Admin && role !== Role.Vendor)) {
      return Promise.resolve();
    }
    const { queryKey, queryFn, staleTime } = foldersQuery(orgId);
    return queryClient.prefetchQuery(queryKey, queryFn, { staleTime });
  }

  async function createFolder(
    orgId: number,
    name: string,
    parentId?: number | null,
  ): Promise<DesignFolder> {
    const dto = await apiClient.createDesignFolder(orgId, { name, parentId });
    await invalidateFolders();
    return mapToDesignFolder(dto);
  }

  async function renameFolder(
    orgId: number,
    folderId: number,
    name: string,
  ): Promise<void> {
    await apiClient.updateDesignFolder(orgId, folderId, { name });
    await invalidateFolders();
  }

  async function deleteFolder(orgId: number, folderId: number): Promise<void> {
    await apiClient.deleteDesignFolder(orgId, folderId);
    await invalidateFolders();
  }

  async function moveFolder(
    orgId: number,
    folderId: number,
    parentId: number | null,
  ): Promise<void> {
    await apiClient.moveDesignFolder(orgId, folderId, { parentId });
    // Item queries are untouched: moving a folder changes the tree, not which
    // folder any design/logo is filed in.
    await invalidateFolders();
  }

  async function reorderFolders(
    orgId: number,
    parentId: number | null,
    orderedIds: number[],
  ): Promise<void> {
    await apiClient.reorderDesignFolders(orgId, { parentId, orderedFolderIds: orderedIds });
    // Item queries are untouched: reordering changes sibling positions, not
    // which folder any design/logo is filed in.
    await invalidateFolders();
  }

  async function moveDesignsToFolder(
    orgId: number,
    designIds: number[],
    folderId: number | null,
  ): Promise<void> {
    await apiClient.moveDesignsToFolder(orgId, { designIds, folderId });
    // Counts on the folder cards AND the currently shown (possibly
    // folder-filtered) design list both change after a move.
    await Promise.all([invalidateFolders(), invalidateDesigns()]);
  }

  async function moveLogosToFolder(
    orgId: number,
    logoIds: number[],
    folderId: number | null,
  ): Promise<void> {
    await apiClient.moveLogosToFolder(orgId, { logoIds, folderId });
    // Counts on the folder cards AND the currently shown (possibly
    // folder-filtered) logo list both change after a move.
    await Promise.all([
      invalidateFolders(),
      queryClient.invalidateQueries({ queryKey: ["logos"], type: "all" }),
    ]);
  }

  return {
    useFolders,
    prefetchFolders,
    createFolder,
    renameFolder,
    deleteFolder,
    moveFolder,
    reorderFolders,
    moveDesignsToFolder,
    moveLogosToFolder,
  };
}
